import { z } from 'zod';
import { uuidSchema } from './common.schema';
import { FeedPrepSplitSecSchema } from './feedPrep.schema';

/* ------------------------------------------------------------------ */
/*  POST /re-dosing  —  Re-split remaining volume from a container     */
/* ------------------------------------------------------------------ */

export const BottleSplitSchema = z.object({
  volume_ml: z.number().positive('Bottle volume must be positive'),
});

export const CreateReDosingSchema = z.object({
  hospital_id:           uuidSchema,
  patient_id:            uuidSchema,
  patient_name:          z.string().min(1).max(200),
  mrn:                   z.string().min(1).max(50),
  container_identifier:  z.string().min(1),              // BTL-P pattern from feed_preparations
  original_volume:       z.number().positive('Original volume must be positive'),
  bottles:               z.array(BottleSplitSchema).min(1, 'At least one bottle is required'),
  remaining_volume:      z.number().min(0, 'Remaining volume cannot be negative'),
  created_by:            uuidSchema,
}).refine(
  (d) => {
    const bottleTotal = d.bottles.reduce((sum, b) => sum + b.volume_ml, 0);
    return Math.abs(bottleTotal + d.remaining_volume - d.original_volume) < 0.01;
  },
  { message: 'Bottles total + remaining volume must equal original volume' },
);
export type CreateReDosing = z.infer<typeof CreateReDosingSchema>;

/* ------------------------------------------------------------------ */
/*  Output — re-dosing response                                        */
/* ------------------------------------------------------------------ */

export const ReDosingSplitSchema = z.object({
  split_id:             uuidSchema,
  split_sequence:       z.number(),
  volume_ml:            z.string(),
  status:               z.string(),
  container_identifier: z.string().nullable(),
  source_split_id:      uuidSchema.nullable().optional(),
  label_expiry_at:      z.string().nullable(),
  created_at:           z.string(),
  sec:                  FeedPrepSplitSecSchema.optional(),
});
export type ReDosingSplit = z.infer<typeof ReDosingSplitSchema>;

/* ------------------------------------------------------------------ */
/*  GET /re-dosing?hospital_id=  —  list query                         */
/* ------------------------------------------------------------------ */

export const ReDosingListQuerySchema = z.object({});
export type ReDosingListQuery = z.infer<typeof ReDosingListQuerySchema>;

/* ------------------------------------------------------------------ */
/*  GET list item — grouped by patient + prep                          */
/* ------------------------------------------------------------------ */

export const ReDosingListItemSchema = z.object({
  prep_id:              uuidSchema,
  patient_id:           uuidSchema.nullable(),
  patient_name:         z.string(),
  mrn:                  z.string(),
  container_identifier: z.string().nullable(),
  number_of_doses:      z.number(),
});
export type ReDosingListItem = z.infer<typeof ReDosingListItemSchema>;

/* ------------------------------------------------------------------ */
/*  POST response                                                      */
/* ------------------------------------------------------------------ */

export const CreateReDosingResponseSchema = z.object({
  prep_id:                    uuidSchema,
  hospital_id:                uuidSchema,
  source_container_identifier: z.string(),
  original_volume:            z.number(),
  remaining_volume:           z.number(),
  recipe_name:                z.string().nullable().optional(),
  splits:                     z.array(ReDosingSplitSchema),
  created_at:                 z.string(),
});
export type CreateReDosingResponse = z.infer<typeof CreateReDosingResponseSchema>;

/* ------------------------------------------------------------------ */
/*  GET /reDosing/splits  —  splits created from a source container   */
/* ------------------------------------------------------------------ */

export const ReDosingByContainerQuerySchema = z.object({
  container_identifier: z.string().min(1),
});
export type ReDosingByContainerQuery = z.infer<typeof ReDosingByContainerQuerySchema>;

export const ReDosingByContainerResponseSchema = z.object({
  source_container_identifier: z.string(),
  prep_id:                     uuidSchema,
  recipe_name:                 z.string().nullable().optional(),
  splits:                      z.array(ReDosingSplitSchema),
});
export type ReDosingByContainerResponse = z.infer<typeof ReDosingByContainerResponseSchema>;
